import { isFilled, type InfoFunction, type Place, type PlaceGroup, type PositionInfo, type PositionsView } from '../crew/positions';
import { dayShort, type Assignment } from '../crew/types';
import type { Recipient } from '../requests/types';

/**
 * Owner 2026-10-28 00:08 (variant A of the mockups, „bez oczoplasu”): what the event panel of „Dostępność → Ludzie”
 * shows, without React — one group per function (the global ⚙ order) with „N z M”, then one row per place: the person
 * (or „wolne”), the status as a dot and ONE word, and at most one action:
 * - „Poproś” — a „Proponowany” not asked yet (or available): a question to this person only, about their days;
 * - „Ponów” — asked, no answer yet: the same again;
 * - „Obsadź” — a free place: the panel's person picker.
 * Rule 7a: what the API leaves out stays out (no status — no word, no action on it; no name — „obsadzone”; no person
 * — nobody to ask). Rule B: nothing is asked or filled on a past day.
 */

export type Tone = 'success' | 'accent' | 'warning' | 'danger' | 'muted';
export type RowStatus = { tone: Tone; word: string };
export type RowAction = 'ask' | 'reask' | 'fill' | null;
/** `send`: a question pinned to the event („Zapytania o dostępność: Dodawanie” and „Obsada: Dodawanie”); `fill`: the person picker. */
export type PanelRights = { send: boolean; fill: boolean };

export type PanelRow = {
  key: string;
  day: string;
  group: PlaceGroup;
  place: Place;
  /** beyond the number (Odmowa, Niedostępny…): a person of the function in no place */
  extra: boolean;
  assignment?: Assignment;
  personId?: string;
  name: string;
  free: boolean;
  status: RowStatus | null;
  action: RowAction;
  /** a collision or „Niedostępny” on this day — the danger mark and its hover text */
  collision: string | null;
  /** „Wymuś” in the row's „⋯” — only with a collision (owner's mockup) */
  mayForce: boolean;
};

export type PanelGroup = { key: string; functionId: string; functionName: string; others: boolean; source: PlaceGroup['source'] | null; filled: number; needed: number; info?: InfoFunction; rows: PanelRow[] };

/** The live question of a person about this event: the newest, not stale, not answered (an answer hidden — still „zapytany”). */
export function pendingQuestion(asked: readonly Recipient[], personId: string): Recipient | null {
  const mine = asked.filter((r) => r.personId === personId && !r.stale);
  const newest = mine.reduce<Recipient | null>((best, r) => (!best || (r.sentAt ?? '') >= (best.sentAt ?? '') ? r : best), null);
  if (!newest) return null;
  if (newest.answer) return null;
  if ((newest.days ?? []).length > 0 && newest.days!.every((d) => d.answer === true || d.answer === false)) return null;
  return newest;
}

const WARSAW = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric' });
/** „zapytany 27.10” (Warsaw day of the send); the date hidden — „zapytany”. */
export const askedWord = (sentAt: string | undefined): string => (sentAt ? `zapytany ${WARSAW.format(new Date(sentAt))}` : 'zapytany');

/** The day the panel opens on: the first from today on, else the first. */
export const firstDay = (days: readonly string[], today: string): string => days.find((d) => d >= today) ?? days[0] ?? today;

/** A live question about this event (not replaced, not stale) — asked, whether answered or not (as the server, review I2). */
export const hasLiveQuestion = (asked: readonly Recipient[], personId: string): boolean => asked.some((r) => r.personId === personId && !r.stale);

/** One person of the server's „Do wysłania” for this event (`GET /api/requests/unsent`). */
export type UnsentOne = { personId: string; name?: string; days?: string[]; noContact?: true };
/** Those of „Do wysłania” a question can go to: a contact, their days known. */
const askable = (unsent: readonly UnsentOne[] | undefined) => (unsent ?? []).filter((p): p is UnsentOne & { days: string[] } => !p.noContact && !!p.days && p.days.length > 0);

/** The days of the event a person works (in a place or beyond the number — review M2), from today on, active. */
export function personDays(view: PositionsView, personId: string, assignments: readonly Assignment[], today: string): string[] {
  const mine = assignments.filter((a) => a.personId === personId);
  const inactive = (day: string) => mine.some((a) => a.days?.some((d) => d.day === day && !d.active));
  return view.days
    .filter((d) => d.day >= today && !inactive(d.day) && d.groups.some((g) => [...g.places, ...(g.extra ?? [])].some((p) => p.personId === personId)))
    .map((d) => d.day);
}

type Input = {
  view: PositionsView;
  info?: PositionInfo | undefined;
  day: string;
  assignments: readonly Assignment[];
  asked: readonly Recipient[];
  /**
   * Review I2: the server's „Do wysłania” people of this event — „Poproś” exactly for them (the server's rule: a live
   * question or a sent confirmation counts as asked; closed events and past days left out). Absent: nobody to ask.
   */
  unsent?: readonly UnsentOne[] | undefined;
  /** The event is cancelled or the client resigned: nothing is asked. */
  closed?: boolean | undefined;
  /** personId → the days marked „Dostępny” (the grid's marks; absent — not known) */
  available?: ReadonlyMap<string, ReadonlySet<string>> | undefined;
  today: string;
  rights: PanelRights;
};

function rowOf(input: Input, g: PlaceGroup, p: Place, extra: boolean): PanelRow {
  const { day, today, rights, view, assignments } = input;
  const a = p.assignmentId ? assignments.find((x) => x.id === p.assignmentId) : undefined;
  const free = !isFilled(p);
  const personId = p.personId ?? a?.personId;
  const key = `${day}:${g.functionId}:${extra ? `x:${p.assignmentId}` : (p.id ?? p.place)}`;
  const base = { key, day, group: g, place: p, extra, ...(a ? { assignment: a } : {}), ...(personId ? { personId } : {}) };
  if (free) {
    const fill = rights.fill && day >= today && (!!p.id || g.source === 'rule');
    return { ...base, name: 'wolne', free: true, status: null, action: fill ? 'fill' : null, collision: null, mayForce: false };
  }
  const name = p.personName ?? a?.personName ?? 'obsadzone';
  const d = a?.days?.find((x) => x.day === day);
  const collision = d?.collision ? `Kolizja ${dayShort(day)}: także „${d.collision.map((c) => c.eventName).join('”, „')}”${d.unavailable ? ' — i niedostępny' : ''}` : d?.unavailable ? `Niedostępny ${dayShort(day)} — a jest w obsadzie` : null;
  const mayForce = !!d?.collision && !d.unavailable && day >= today;
  const status = a?.status;
  let shown: RowStatus | null = null;
  let action: RowAction = null;
  if (status === 'odmowa') shown = { tone: 'danger', word: 'odmowa' };
  else if (d && !d.active && !d.collision) shown = { tone: 'muted', word: 'nieaktywny' };
  else if (status === 'potwierdzony') shown = { tone: 'success', word: 'potwierdzony' };
  else if (status === 'proponowany') {
    const days = personId ? personDays(view, personId, assignments, today) : [];
    const pending = personId ? pendingQuestion(input.asked, personId) : null;
    const mayAsk = rights.send && !input.closed && !!personId && days.length > 0 && day >= today;
    const unsent = personId ? askable(input.unsent).find((u) => u.personId === personId) : undefined;
    if (pending) {
      shown = { tone: 'warning', word: askedWord(pending.sentAt) };
      action = mayAsk ? 'reask' : null;
    } else {
      const marks = personId ? input.available?.get(personId) : undefined;
      const available = !!marks && days.length > 0 && days.every((x) => marks.has(x));
      shown = { tone: 'accent', word: available ? 'dostępny' : 'proponowany' };
      // not asked (as the server counts it) — „Poproś”; asked and answered (in part, or „tak” held off) — „Ponów”
      action = !mayAsk ? null : unsent ? 'ask' : hasLiveQuestion(input.asked, personId!) ? 'reask' : null;
    }
  }
  return { ...base, name, free: false, status: shown, action, collision, mayForce };
}

/** The groups of `day`: the ⚙ functions in their order (also those with no places — their number can be raised), then any other function with places. */
export function panelGroups(input: Input): PanelGroup[] {
  const groups = input.view.days.find((d) => d.day === input.day)?.groups ?? [];
  const byFn = new Map(groups.map((g) => [g.functionId, g]));
  const fns = input.info?.functions ?? [];
  const order = [...fns.map((f) => f.functionId), ...groups.map((g) => g.functionId).filter((id) => !fns.some((f) => f.functionId === id))];
  return order.flatMap((functionId): PanelGroup[] => {
    const g = byFn.get(functionId);
    const f = fns.find((x) => x.functionId === functionId);
    if (!g) {
      if (!f) return [];
      return [{ key: functionId, functionId, functionName: f.functionName, others: f.others, source: null, filled: 0, needed: 0, info: f, rows: [] }];
    }
    const rows = [...g.places.map((p) => rowOf(input, g, p, false)), ...(g.extra ?? []).map((p) => rowOf(input, g, p, true))];
    return [{ key: functionId, functionId, functionName: g.functionName, others: g.others, source: g.source, filled: g.places.filter(isFilled).length, needed: g.places.length, ...(f ? { info: f } : {}), rows }];
  });
}

/** „Poproś niepotwierdzonych” (review I2): the server's „Do wysłania” people of this event who can be asked, by name. */
export function toAsk(input: { unsent: readonly UnsentOne[] | undefined; closed?: boolean | undefined; rights: PanelRights }): Array<{ personId: string; name?: string; days: string[] }> {
  if (!input.rights.send || input.closed) return [];
  return askable(input.unsent)
    .map((p) => ({ personId: p.personId, ...(p.name ? { name: p.name } : {}), days: p.days }))
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pl'));
}
